import { useMemo } from "react";
import { useDispatch } from "react-redux";

import { useQuery } from "@tanstack/react-query";

import { apiThatThrows } from "containers/Request";
import { transformManifestResponse } from "hooks/manifests/transformers";
import { Manifest, ManifestGroup, ManifestRaw } from "hooks/manifests/types";
import { ApiQueryParams, ApiResponse } from "types/api";

const ITEMS_LIMIT = 100;
const PAGINATION_LIMIT = 20;
const DEFAULT_SORT_ORDER = "-modified_date";

const groupManifestsByProvider = (
  allManifests: ManifestRaw[]
): ManifestGroup[] => {
  const manifestsByProvider = new Map<string, Manifest[]>();

  allManifests.forEach((rawManifest) => {
    const manifest = transformManifestResponse({
      results: rawManifest,
    }).results;

    const providerId = manifest.provider_id;
    const manifests = manifestsByProvider.get(providerId) ?? [];

    manifests.push(manifest);
    manifestsByProvider.set(providerId, manifests);
  });

  return Array.from(manifestsByProvider, ([providerId, manifests]) => ({
    providerId,
    manifests,
  }));
};

export const useManifestsGroups = (query?: ApiQueryParams) => {
  const dispatch = useDispatch();
  // Memoize the base query params so pagination changes do not trigger a refetch.
  const apiQueryParams = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(query ?? {}).filter(
          ([key, value]) =>
            value !== undefined && !["page", "limit", "offset"].includes(key)
        )
      ) as ApiQueryParams,
    [query]
  );

  return useQuery<ApiResponse<ManifestGroup[]>>({
    queryKey: ["manifests", apiQueryParams],
    queryFn: async () => {
      const allManifests: ManifestRaw[] = [];
      let page = 1;

      while (true) {
        const response: ApiResponse<ManifestRaw[]> = await dispatch(
          apiThatThrows.getAppManifestList.action({
            queryParams: {
              order: DEFAULT_SORT_ORDER,
              ...apiQueryParams,
              limit: ITEMS_LIMIT,
              page,
            },
          })
        );

        const results = response.results ?? [];
        allManifests.push(...results);

        const total = response.meta?.total ?? allManifests.length;
        if (!results.length || allManifests.length >= total) {
          break;
        }
        page++;
      }

      const groupedManifests = groupManifestsByProvider(allManifests);

      return {
        meta: {
          total: groupedManifests.length,
        },
        results: groupedManifests,
      };
    },
    select: (data: ApiResponse<ManifestGroup[]>) => {
      // Apply client-side pagination to the grouped results.
      const localLimit = Number(query?.limit ?? PAGINATION_LIMIT);
      const start = (Number(query?.page ?? 1) - 1) * localLimit;
      const results = (data.results ?? []).slice(start, start + localLimit);

      return {
        meta: {
          total: data.meta?.total ?? (data.results ?? []).length,
        },
        results,
      };
    },
  });
};
